import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShieldCheckIcon, InfinityIcon, PackageCheckIcon } from 'lucide-react';
import { LensId, builderAssets, lensById, lensClipPaths, remainingLenses } from '../../data/builder';
import { BuildState } from '../../utils/builderCart';
interface BuilderVisualProps {
  state: BuildState;
  /** The loop only plays on the lens system step; afterwards the 3 lenses stay as a still. */
  animateLenses?: boolean;
}
interface ExtraTile {
  key: string;
  src: string;
  label: string;
  alt: string;
  filter?: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;
/** Background of the real studio photos, so the stage blends into them. */
const STUDIO_BG = '#0e0e0f';
type CycleFrame = LensId | 'all';
/** Frames for the "all 3 lenses" animation: each lens, then all three together. */
const LENS_CYCLE: CycleFrame[] = ['smoke', 'amber', 'clear', 'all'];
const FRAME_MS: Record<CycleFrame, number> = {
  smoke: 1300,
  amber: 1300,
  clear: 1300,
  all: 2600
};

/**
 * Live product stage. With the real studio photos, the X1 is shown on a light
 * studio stage and each add-on appears as a tile underneath. Without them it
 * falls back to the dark composited layers with a lens tint.
 */
export function BuilderVisual({
  state,
  animateLenses = false
}: BuilderVisualProps) {
  const reduce = useReducedMotion();
  const lens = lensById(state.lens);
  const realLensPhoto = builderAssets.x1ByLens[state.lens];
  const showSpares = state.complete || state.fullLens;
  const showCase = state.complete || state.hardCase;
  const spares = remainingLenses(state.lens);
  const cycling = Boolean(realLensPhoto) && showSpares && animateLenses && !reduce;
  const [cycleIndex, setCycleIndex] = useState(0);

  // With all 3 lenses, loop Smoke → Amber → Clear → all three together.
  // The loop starts on the customer's own lens, so turning it on never jumps to another photo.
  useEffect(() => {
    if (cycling) setCycleIndex(Math.max(0, LENS_CYCLE.indexOf(state.lens)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycling]);
  useEffect(() => {
    if (!cycling) return;
    const timer = window.setTimeout(() => setCycleIndex(i => (i + 1) % LENS_CYCLE.length), FRAME_MS[LENS_CYCLE[cycleIndex]]);
    return () => window.clearTimeout(timer);
  }, [cycling, cycleIndex]);

  // Outside the lens system step (or with reduced motion), the three lenses stay together as a still.
  const shownFrame: CycleFrame = showSpares && realLensPhoto ? cycling ? LENS_CYCLE[cycleIndex] : 'all' : state.lens;

  // Remember the frame that was on screen before, so it stays visible under the incoming one.
  const frameRef = useRef<{
    current: CycleFrame;
    previous: CycleFrame;
  }>({
    current: shownFrame,
    previous: shownFrame
  });
  if (frameRef.current.current !== shownFrame) {
    frameRef.current = {
      previous: frameRef.current.current,
      current: shownFrame
    };
  }
  const previousFrame = frameRef.current.previous;

  // Preload every lens photo so switching is instant.
  useEffect(() => {
    ;
    [...Object.values(builderAssets.x1ByLens), builderAssets.fullLensSystem].forEach(src => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, []);
  const layer = {
    initial: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 8
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0
    },
    exit: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 8
    },
    transition: {
      duration: 0.24,
      ease: EASE
    }
  };
  const badges = <>
      <div className="absolute left-3 top-3 z-10 flex flex-col items-start gap-1.5 sm:left-4 sm:top-4" data-id="element-213" data-mp="30jg7l7gvosf7">
        <AnimatePresence mode="popLayout" data-id="element-214" data-mp="2wlsb3zzjerl7">
          <motion.span key={`${state.complete}-${state.protection}`} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} className={`inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[0.16em] sm:text-[10px] ${state.protection === 'lifetime' ? 'gold-plate text-ink-950' : 'border border-white/20 bg-ink-950/85 text-steel-200'}`} data-id="element-215" data-mp="2rlnw6eea24x3">
            {state.protection === 'lifetime' ? <InfinityIcon className="h-3 w-3" aria-hidden="true" data-id="element-216" data-mp="2z43ulmk2vbrv" /> : <ShieldCheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-217" data-mp="3mty7230hiybl" />}
            {state.protection === 'lifetime' ? 'Lifetime' : state.complete || state.protection === 'oneYear' ? '1-year' : '30-day'}{' '}
            coverage
          </motion.span>
        </AnimatePresence>
      </div>
      <AnimatePresence data-id="element-218" data-mp="22d1mywwgb4ia">
        {state.complete && <motion.span key="complete" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 bg-grit-500 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[0.16em] text-ink-950 sm:right-4 sm:top-4 sm:text-[10px]" data-id="element-219" data-mp="22bgqv2uhndib">
            <PackageCheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-220" data-mp="23ugtk3wwfg8r" />
            Complete System
          </motion.span>}
      </AnimatePresence>
    </>;
  if (realLensPhoto) {
    const extras: ExtraTile[] = [...(showCase ? [{
      key: 'case',
      src: builderAssets.hardCase,
      label: 'Hard case',
      alt: 'Hard protective case'
    }] : []), ...(state.careKit ? [{
      key: 'care',
      src: builderAssets.careKit,
      label: 'Care kit',
      alt: 'Anti-fog care kit with microfiber cloth'
    }] : []), ...(state.rx > 0 ? [{
      key: 'rx',
      src: builderAssets.rxInsert,
      label: 'RX insert',
      alt: 'RX Capability Insert'
    }] : [])];
    const hasExtras = extras.length > 0;
    const frames: CycleFrame[] = ['clear', 'amber', 'smoke', 'all'];
    const srcFor = (frame: CycleFrame) => frame === 'all' ? builderAssets.fullLensSystem : builderAssets.x1ByLens[frame] || realLensPhoto;
    return <div className="w-full" data-id="element-221" data-mp="326l08824ahmt">
      <div className="relative aspect-[4/3] w-full overflow-hidden border border-white/10" style={{
        backgroundColor: STUDIO_BG
      }} data-id="element-222" data-mp="328e97u07ddxz">
        {/* The real X1. Every photo stays mounted and stacked; the incoming one fades in over the
            previous one, which stays fully visible underneath, so there is no dark dip or flash. */}
        <motion.div className="absolute inset-x-0 w-full" initial={false} animate={{
          top: hasExtras ? '-29%' : '-16.7%'
        }} transition={{
          duration: reduce ? 0 : 0.26,
          ease: EASE
        }} data-id="element-223" data-mp="2ejd4agqkwkxg">
          <div className="relative aspect-square w-full" data-id="element-224" data-mp="3tysfp55vznzm">
            {frames.map(frame => {
              const isCurrent = frame === shownFrame;
              const isPrevious = frame === previousFrame && !isCurrent;
              return <img key={frame} src={srcFor(frame)} alt={isCurrent ? frame === 'all' ? 'GritVue X1 with the Smoke, Amber and Clear lenses side by side' : showSpares ? `GritVue X1 shown with the ${lensById(frame).name} lens, one of your 3 lenses` : `GritVue X1 with the ${lens.name} lens` : ''} aria-hidden={!isCurrent || undefined} decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{
                opacity: isCurrent || isPrevious ? 1 : 0,
                zIndex: isCurrent ? 2 : isPrevious ? 1 : 0,
                transition: reduce ? 'none' : isCurrent ? 'opacity 280ms cubic-bezier(0.23, 1, 0.32, 1)' : 'none'
              }} data-id="element-225" data-mp="cv4p0qfhbq5o" />;
            })}
          </div>
        </motion.div>

        {/* Add-ons appear as tiles under the X1 */}
        <ul className="absolute inset-x-3 bottom-3 z-10 flex flex-wrap justify-center gap-2 sm:inset-x-4 sm:bottom-4" aria-label="Added to your X1" data-id="element-226" data-mp="1xvume8bqaop3">
          <AnimatePresence initial={false} data-id="element-227" data-mp="1p0eu3vqmghlm">
            {extras.map(item => <motion.li key={item.key} layout={!reduce} {...layer} className="w-[17%] min-w-[64px] max-w-[120px] overflow-hidden border border-ink-950/10 bg-ink-950 shadow-[0_6px_16px_rgba(10,10,10,0.18)]" data-id="element-228" data-mp="1a45vmwek28a9">
                <img src={item.src} alt={item.alt} className="aspect-square w-full object-cover" style={item.filter ? {
                filter: item.filter
              } : undefined} data-id="element-229" data-mp="162bxovvqvids" />
                <span className="block truncate px-1 py-1 text-center text-[8px] font-extrabold uppercase tracking-[0.12em] text-steel-200 sm:text-[10px]" data-id="element-230" data-mp="2vfp7v1qjg0mt">
                  {item.label}
                </span>
              </motion.li>)}
          </AnimatePresence>
        </ul>

        <div className="relative z-10" data-id="element-231" data-mp="35njpb14uxmx0">{badges}</div>

        {/* Compact "all 3 lenses" chip; the lens on screen lights up. Kept small so the sticky
            photo never grows and the step buttons stay in view. */}
        <AnimatePresence data-id="element-232" data-mp="3ekk90ugzewu4">
          {showSpares && !state.complete && <motion.span key="all-lenses" initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.18,
            ease: EASE
          }} className="absolute right-3 top-3 z-10 inline-flex items-center gap-2 bg-ink-950/90 px-2 py-1 text-[9px] font-extrabold uppercase tracking-[0.14em] text-offwhite sm:right-4 sm:top-4 sm:text-[10px]" data-id="element-233" data-mp="3t9q6oe068ezw">
              3 lenses
              <span className="flex items-center gap-1" aria-hidden="true" data-id="element-234" data-mp="23xxte4f5zrrw">
                {(['clear', 'amber', 'smoke'] as LensId[]).map(id => {
                const active = shownFrame === 'all' || id === shownFrame;
                return <span key={id} className={`h-2 w-2 rounded-full border transition-[transform,opacity] duration-200 ease-out ${active ? 'scale-125 border-white/70 opacity-100' : 'border-white/30 opacity-40'}`} style={{
                  backgroundColor: lensById(id).swatch
                }} data-id="element-235" data-mp="3uri0qjd2iixs" />;
              })}
              </span>
            </motion.span>}
        </AnimatePresence>

        {!hasExtras && !showSpares && <span className="absolute bottom-3 right-3 z-10 bg-ink-950/85 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-steel-200 sm:text-[10px]" data-id="element-236" data-mp="ckas4ht479xm">
            {lens.name} lens
          </span>}
      </div>

      </div>;
  }
  return <div className="relative aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-950" data-id="element-237" data-mp="1cna6hefivpu6">
      {/* Hard case sits behind the frame */}
      <AnimatePresence data-id="element-238" data-mp="1hn4wzgvdydep">
        {showCase && <motion.img key="case" {...layer} src={builderAssets.hardCase} alt="Hard protective case" className="absolute left-[58%] top-[46%] w-[40%] mix-blend-lighten" data-id="element-239" data-mp="1mjd5ibf2v8cw" />}
      </AnimatePresence>

      {/* X1 frame + selected lens */}
      <div className="absolute left-[6%] top-[-2%] w-[88%]" data-id="element-240" data-mp="20iuyz43bj9sl">
        <div className="relative" data-id="element-241" data-mp="25acrd3ra021y">
          <img src={builderAssets.x1Base} alt={`GritVue X1 with the ${lens.name} lens`} className="block w-full mix-blend-lighten" loading="eager" data-id="element-242" data-mp="25c7fss1racas" />
          {lensClipPaths.map(clip => <motion.span key={clip} aria-hidden="true" className="absolute inset-0" style={{
          clipPath: clip,
          mixBlendMode: lens.tint.blend
        }} animate={{
          backgroundColor: lens.tint.color
        }} transition={{
          duration: reduce ? 0 : 0.22,
          ease: EASE
        }} data-id="element-243" data-mp="2fadd0wcmau9b" />)}
        </div>
      </div>

      {/* RX insert, shown behind the main lens */}
      <AnimatePresence data-id="element-244" data-mp="t0bxuuxeo8mf">
        {state.rx > 0 && <motion.div key="rx" {...layer} className="absolute left-[33%] top-[36%] w-[30%]" data-id="element-245" data-mp="o6g301cfv1vh">
            <img src={builderAssets.rxInsert} alt="RX Capability Insert behind the main lens" className="w-full opacity-80 mix-blend-lighten" data-id="element-246" data-mp="12vlypfc2i9ym" />
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap border border-[#7fe3ec]/60 bg-ink-950/90 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.16em] text-[#7fe3ec] sm:text-[10px]" data-id="element-247" data-mp="xzerlj7xqsf3">
              RX insert behind lens
            </span>
          </motion.div>}
      </AnimatePresence>

      {/* The two extra lenses */}
      <AnimatePresence data-id="element-248" data-mp="39o67vtgyyywl">
        {showSpares && spares.map((spare, index) => <motion.div key={`spare-${spare.id}`} {...layer} transition={{
        ...layer.transition,
        delay: reduce ? 0 : index * 0.05
      }} className="absolute top-[60%] w-[22%]" style={{
        left: `${2 + index * 17}%`
      }} data-id="element-249" data-mp="2zk7pu15sw42t" data-mpi={index}>
              <img src={builderAssets.spareLens} alt={`${spare.name} lens`} className="w-full mix-blend-lighten" style={{
          filter: spare.spareFilter
        }} data-id="element-250" data-mp="2ur69rptuxyhn" />
              <span className="absolute bottom-[6%] left-1/2 -translate-x-1/2 text-[9px] font-extrabold uppercase tracking-[0.18em] text-steel-300 sm:text-[10px]" data-id="element-251" data-mp="2ku0fk8py5rd7">
                {spare.name}
              </span>
            </motion.div>)}
      </AnimatePresence>

      {/* Care kit */}
      <AnimatePresence data-id="element-252" data-mp="2bv6kwnw6fb2a">
        {state.careKit && <motion.img key="care" {...layer} src={builderAssets.careKit} alt="Anti-fog care kit with microfiber cloth" className="absolute left-[38%] top-[62%] w-[24%] mix-blend-lighten" data-id="element-253" data-mp="5475vy9og31n" />}
      </AnimatePresence>

      {badges}

      <span className="absolute bottom-2 right-3 text-[9px] font-bold uppercase tracking-[0.18em] text-steel-500 sm:text-[10px]" data-id="element-254" data-mp="216k1q336wlle">
        {lens.name} lens{showSpares ? ' + 2 more' : ''}
      </span>
    </div>;
}

/** The X1 wearing one specific lens, used for the visual lens choice cards. */
export function X1LensImage({
  lensId
}: {
  lensId: LensId;
}) {
  const lens = lensById(lensId);
  const real = builderAssets.x1ByLens[lensId];
  if (real) {
    return <span className="relative block aspect-[4/3] overflow-hidden" style={{
      backgroundColor: STUDIO_BG
    }} data-id="element-255" data-mp="1dfwnz16lf1qx">
        <img src={real} alt={`GritVue X1 with the ${lens.name} lens`} loading="lazy" className="absolute inset-x-0 top-[-16.7%] block w-full" data-id="element-256" data-mp="18i4qzsr3cpfv" />
      </span>;
  }
  return <span className="relative block aspect-[4/3] overflow-hidden bg-ink-950" data-id="element-257" data-mp="zh6v5xqt3o8d">
      <span className="absolute left-[-4%] top-[-6%] block w-[108%]" data-id="element-258" data-mp="3b2yxybcjtksp">
        <span className="relative block" data-id="element-259" data-mp="3g2v3if0w9kad">
          <img src={builderAssets.x1Base} alt={`GritVue X1 with the ${lens.name} lens`} loading="lazy" className="block w-full mix-blend-lighten" data-id="element-260" data-mp="3g1sfceuyhvy5" />
          {lensClipPaths.map(clip => <span key={clip} aria-hidden="true" className="absolute inset-0" style={{
          clipPath: clip,
          mixBlendMode: lens.tint.blend,
          backgroundColor: lens.tint.color
        }} data-id="element-261" data-mp="3p2i4x0tmiq3f" />)}
        </span>
      </span>
    </span>;
}